import type { ShopifyProduct } from '../types/shopify-product'
import { ShopifyProductService } from '../services/shopify-product.service'

/**
 * Merges two objects, adding properties from the second object only if they don't exist in the first object
 * @param target The primary object
 * @param source The secondary object whose properties will be added if missing in target
 * @returns A new object containing the merged properties
 */
export function mergeIfMissing<T extends object, U extends object>(
  target: T,
  source: U,
): T & Partial<U> {
  // Null/undefined check
  if (!source) return { ...target } as T & Partial<U>

  // Use Object.entries for better performance with larger objects
  return Object.entries(source).reduce(
    (result, [key, value]) => ({
      ...result,
      ...(!result.hasOwnProperty(key) ? { [key]: value } : {}),
    }),
    { ...target },
  ) as T & Partial<U>
}

/**
 * Merges two Shopify products and their variants
 * @param product1 The primary product
 * @param product2 The secondary product whose properties will be merged
 * @returns A new product containing the merged properties and variants
 */
export function mergeShopifyProducts(
  product1: ShopifyProduct,
  product2: ShopifyProduct,
): ShopifyProduct {
  // First merge the main product properties
  const mergedProduct = mergeIfMissing(product1, product2)

  for (let i = 0; i < mergedProduct.variants.length; i++) {
    const variant2 = ShopifyProductService.getVariantById(
      product2,
      mergedProduct.variants[i].id,
    )
    if (variant2 && mergedProduct.variants[i] !== variant2) {
      mergedProduct.variants[i] = mergeIfMissing(
        mergedProduct.variants[i],
        variant2,
      )
    }
  }

  return mergedProduct
}
